import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquareText, X, Send, Sparkles, Loader2, UploadCloud, PhoneCall } from "lucide-react";
import { useUI } from "../context/UIContext";
import { sendChat } from "../lib/api";

const GREETING =
  "Hallo! Ich bin Ihr Energie-Assistent. Ich helfe Ihnen bei Fragen zu Strom, Gas, Photovoltaik, Wärmepumpe oder einem Anbieterwechsel. Womit kann ich Ihnen helfen?";

const SUGGESTIONS = [
  "Wie viel kann ich beim Stromtarif sparen?",
  "Wie läuft ein Anbieterwechsel ab?",
  "Ich brauche Gewerbestrom für meinen Betrieb",
];

const uuid = () =>
  (crypto?.randomUUID?.() ||
    "sess-" + Date.now() + "-" + Math.random().toString(16).slice(2));

export const ChatAssistant = () => {
  const { openUpload, openLead } = useUI();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uuid);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const history = messages.slice(-10);
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const data = await sendChat({ session_id: sessionId, message: content, history });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Entschuldigung, da ist etwas schiefgelaufen. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt per WhatsApp." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-launcher"
        aria-label="Energie-Assistent öffnen"
        className="fixed bottom-24 right-6 z-[92] h-14 w-14 grid place-items-center rounded-full bg-blue-700 text-white shadow-2xl shadow-blue-900/40 hover:bg-blue-600 hover:scale-110 transition-[transform,background-color] duration-300"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquareText className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && <span className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-20" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.19, 1, 0.22, 1] }}
            className="fixed bottom-44 right-6 z-[92] w-[calc(100vw-3rem)] sm:w-[400px] max-h-[70vh] flex flex-col rounded-3xl bg-white border border-slate-200 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.35)] overflow-hidden"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="bg-blue-700 px-5 py-4 text-white flex items-center gap-3 shrink-0">
              <span className="h-9 w-9 rounded-full bg-white/15 grid place-items-center"><Sparkles className="h-4.5 w-4.5" /></span>
              <div className="leading-tight">
                <p className="font-heading font-medium">Energie-Assistent</p>
                <p className="text-[11px] text-blue-100 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Antwortet in Sekunden</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-blue-700 text-white rounded-br-sm" : "bg-white border border-slate-200 text-navy-900 rounded-bl-sm"
                    }`}
                    data-testid={`chat-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="chat-typing">
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3">
                    <span className="flex gap-1">
                      {[0, 1, 2].map((d) => (
                        <span key={d} className="h-2 w-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                      ))}
                    </span>
                  </div>
                </div>
              )}

              {messages.length <= 1 && !loading && (
                <div className="pt-1 space-y-2" data-testid="chat-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="block w-full text-left text-sm rounded-xl border border-blue-200 bg-white text-blue-700 px-3.5 py-2.5 hover:bg-blue-50 transition-colors"
                      data-testid="chat-suggestion"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div className="flex gap-2 px-4 pt-3 shrink-0 bg-white">
              <button onClick={() => { setOpen(false); openUpload(); }} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium py-2 hover:bg-blue-100 transition-colors" data-testid="chat-upload-action">
                <UploadCloud className="h-3.5 w-3.5" /> Rechnung prüfen
              </button>
              <button onClick={() => { setOpen(false); openLead({ type: "rueckruf", topic: "chat" }); }} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium py-2 hover:bg-blue-100 transition-colors" data-testid="chat-lead-action">
                <PhoneCall className="h-3.5 w-3.5" /> Rückruf
              </button>
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 p-4 shrink-0 bg-white"
              data-testid="chat-input-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ihre Nachricht…"
                className="flex-1 rounded-full bg-slate-100 focus:bg-white border border-transparent focus:border-blue-300 outline-none px-4 py-3 text-sm transition-colors"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="h-11 w-11 shrink-0 grid place-items-center rounded-full bg-blue-700 text-white disabled:opacity-50 hover:bg-blue-600 transition-colors"
                data-testid="chat-send-btn"
              >
                {loading ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <Send className="h-4.5 w-4.5" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
